import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from './api';
import useAuth from '../hooks/useAuth';
import { FaEye, FaEyeSlash, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

/* ── Password policy helpers ─────────────────────────────────────────────── */
const rules = [
  { key: 'len',     label: 'At least 8 characters',          test: pw => pw.length >= 8 },
  { key: 'upper',   label: 'At least 1 uppercase letter (A-Z)', test: pw => /[A-Z]/.test(pw) },
  { key: 'special', label: 'At least 1 special character (!@#$...)', test: pw => /[^A-Za-z0-9]/.test(pw) },
];

export default function ChangePassword() {
  const { auth } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const forced = location.state?.forced || false;

  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [saving, setSaving] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const allPass = rules.every(r => r.test(newPwd));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!currentPwd || !newPwd) return alert('Please fill all fields');
    if (!allPass) return alert('New password does not meet all the requirements listed below.');
    if (newPwd !== confirmPwd) return alert('New passwords do not match');

    setSaving(true);
    try {
      await api.post(`${process.env.REACT_APP_URL}/users/change-password`, {
        username: auth.username,
        currentPassword: currentPwd,
        newPassword: newPwd,
      });
      alert('Password changed successfully!');
      setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
      if (forced) navigate('/ListPatients', { replace: true });
    } catch (err) {
      alert(err.response?.data?.message || err.response?.data || 'Error changing password');
    } finally {
      setSaving(false);
    }
  }

  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: 14, marginTop: 4, boxSizing: 'border-box' };
  const eyeStyle = { position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 15, cursor: 'pointer', zIndex: 2 };

  return (
    <div style={{ padding: 16, maxWidth: 420, margin: '40px auto' }}>
      {/* Forced-mode banner */}
      {forced && (
        <div style={{
          background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 10,
          padding: '14px 16px', marginBottom: 20, fontSize: 13, lineHeight: 1.55, color: '#92400e',
        }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Password Update Required</div>
          Your current password does not meet the security requirements.
          Please set a new password that follows the rules below before continuing.
        </div>
      )}

      <h2 style={{ marginBottom: 6, fontSize: 20, fontWeight: 800, color: '#1e293b' }}>
        {forced ? 'Set a Strong Password' : 'Change Password'}
      </h2>
      <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>
        {forced ? 'You must update your password to continue using the system.' : 'Enter your current password and choose a new one.'}
      </p>

      {/* ── Password Rules ── */}
      <div style={{
        background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
        padding: '12px 16px', marginBottom: 20,
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '.3px', marginBottom: 8 }}>
          Password Requirements
        </div>
        {rules.map(r => {
          const pass = newPwd ? r.test(newPwd) : null;
          return (
            <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 5, color: pass === null ? '#64748b' : pass ? '#059669' : '#dc2626' }}>
              {pass === null
                ? <span style={{ width: 16, height: 16, borderRadius: '50%', border: '1.5px solid #cbd5e1', display: 'inline-block', flexShrink: 0 }} />
                : pass
                  ? <FaCheckCircle style={{ fontSize: 15, flexShrink: 0 }} />
                  : <FaTimesCircle style={{ fontSize: 15, flexShrink: 0 }} />
              }
              {r.label}
            </div>
          );
        })}
      </div>

      <form onSubmit={handleSubmit}>
        {/* Current Password */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>Current Password</label>
          <div style={{ position: 'relative' }}>
            <input type={showCurrent ? 'text' : 'password'} value={currentPwd} onChange={e => setCurrentPwd(e.target.value)}
              style={{ ...inputStyle, paddingRight: 40 }} required />
            {showCurrent
              ? <FaEyeSlash onClick={() => setShowCurrent(false)} style={eyeStyle} />
              : <FaEye onClick={() => setShowCurrent(true)} style={eyeStyle} />
            }
          </div>
        </div>

        {/* New Password */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>New Password</label>
          <div style={{ position: 'relative' }}>
            <input type={showNew ? 'text' : 'password'} value={newPwd} onChange={e => setNewPwd(e.target.value)}
              style={{ ...inputStyle, paddingRight: 40, borderColor: newPwd && !allPass ? '#fbbf24' : newPwd && allPass ? '#10b981' : '#d1d5db' }} required />
            {showNew
              ? <FaEyeSlash onClick={() => setShowNew(false)} style={eyeStyle} />
              : <FaEye onClick={() => setShowNew(true)} style={eyeStyle} />
            }
          </div>
        </div>

        {/* Confirm */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>Confirm New Password</label>
          <div style={{ position: 'relative' }}>
            <input type={showConfirm ? 'text' : 'password'} value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)}
              style={{ ...inputStyle, paddingRight: 40, borderColor: confirmPwd && confirmPwd !== newPwd ? '#ef4444' : confirmPwd && confirmPwd === newPwd ? '#10b981' : '#d1d5db' }} required />
            {showConfirm
              ? <FaEyeSlash onClick={() => setShowConfirm(false)} style={eyeStyle} />
              : <FaEye onClick={() => setShowConfirm(true)} style={eyeStyle} />
            }
          </div>
          {confirmPwd && confirmPwd !== newPwd && (
            <div style={{ fontSize: 12, color: '#ef4444', marginTop: 4 }}>Passwords do not match</div>
          )}
        </div>

        <button type="submit" disabled={saving || !allPass || !confirmPwd || confirmPwd !== newPwd}
          style={{
            width: '100%', padding: '12px', borderRadius: 8, border: 'none',
            background: allPass && confirmPwd === newPwd ? '#4f46e5' : '#a5b4fc',
            color: '#fff', fontSize: 15, fontWeight: 700, cursor: allPass ? 'pointer' : 'not-allowed',
            transition: 'background .15s',
          }}>
          {saving ? 'Saving...' : forced ? 'Set Password & Continue' : 'Change Password'}
        </button>
      </form>
    </div>
  );
}
